import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useOportunidadeUpsert } from '@/api/mutations/oportunidade';
import { useEmpresaServicos } from '@/api/queries/empresa';
import type { Oportunidade } from '@/api/types';

const ESTAGIOS = [
  { id: 1, nome: 'Lista' },
  { id: 2, nome: 'Abertura' },
  { id: 3, nome: 'Qualificação' },
  { id: 4, nome: 'Call diagnóstico' },
  { id: 5, nome: 'Diagnóstico pago' },
  { id: 6, nome: 'Proposta' },
  { id: 7, nome: 'Objeção' },
  { id: 8, nome: 'Fechamento' },
  { id: 9, nome: 'Onboarding' },
  { id: 10, nome: 'Retenção' },
  { id: 11, nome: 'Perdido' },
  { id: 12, nome: 'Dead Onb' },
];

interface OportunidadeFormProps {
  empresaId: string;
  oportunidade?: Oportunidade | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OportunidadeForm({
  empresaId,
  oportunidade,
  open,
  onOpenChange,
}: OportunidadeFormProps) {
  const upsert = useOportunidadeUpsert();
  const { data: servicos } = useEmpresaServicos(empresaId);
  const [estagioId, setEstagioId] = useState(3);
  const [servicoId, setServicoId] = useState('');
  const [valor, setValor] = useState('');
  const [vazamento, setVazamento] = useState('');
  const [probabilidade, setProbabilidade] = useState('50');
  const [dataAcao, setDataAcao] = useState('');
  const [acaoDesc, setAcaoDesc] = useState('');
  const [proxMovimento, setProxMovimento] = useState('');

  useEffect(() => {
    if (open) {
      if (oportunidade) {
        setEstagioId(oportunidade.estagio.id);
        setServicoId(oportunidade.servico?.id ?? '');
        setValor(oportunidade.valor_estimado_mensal_brl?.toString() ?? '');
        setVazamento(oportunidade.valor_vazamento_quantificado_brl_mes?.toString() ?? '');
        setProbabilidade(oportunidade.probabilidade?.toString() ?? '50');
        setDataAcao(oportunidade.data_proxima_acao ?? '');
        setAcaoDesc(oportunidade.acao_descricao ?? '');
        setProxMovimento(oportunidade.proximo_movimento_recomendado ?? '');
      } else {
        setEstagioId(3);
        setServicoId('');
        setValor('');
        setVazamento('');
        setProbabilidade('50');
        setDataAcao('');
        setAcaoDesc('');
        setProxMovimento('');
      }
    }
  }, [open, oportunidade]);

  async function submit() {
    try {
      await upsert.mutateAsync({
        empresa_id: empresaId,
        id: oportunidade?.id,
        payload: {
          estagio_id: estagioId,
          servico_id: servicoId || undefined,
          valor_estimado_mensal_brl: valor ? parseFloat(valor) : undefined,
          valor_vazamento_quantificado_brl_mes: vazamento ? parseFloat(vazamento) : undefined,
          probabilidade: probabilidade ? parseInt(probabilidade, 10) : undefined,
          data_proxima_acao: dataAcao || undefined,
          acao_descricao: acaoDesc || undefined,
          proximo_movimento_recomendado: proxMovimento || undefined,
        },
      });
      toast.success(oportunidade ? 'Oportunidade atualizada' : 'Oportunidade criada');
      onOpenChange(false);
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <div className="space-y-4 max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold">
            {oportunidade ? 'Editar oportunidade' : 'Nova oportunidade'}
          </h3>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Estágio</label>
              <select
                value={estagioId}
                onChange={(e) => setEstagioId(parseInt(e.target.value, 10))}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {ESTAGIOS.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Serviço</label>
              <select
                value={servicoId}
                onChange={(e) => setServicoId(e.target.value)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— sem serviço</option>
                {servicos?.map((s) => (
                  <option key={s.servico.id} value={s.servico.id}>
                    {s.servico.nome} (fit {s.fit_score})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Valor mensal R$</label>
              <Input
                type="number"
                step="0.01"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Vazamento quantificado R$/mês
              </label>
              <Input
                type="number"
                step="0.01"
                value={vazamento}
                onChange={(e) => setVazamento(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Probabilidade %</label>
              <Input
                type="number"
                min="0"
                max="100"
                value={probabilidade}
                onChange={(e) => setProbabilidade(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Próxima ação</label>
              <Input
                type="date"
                value={dataAcao}
                onChange={(e) => setDataAcao(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Descrição da ação</label>
            <Input
              value={acaoDesc}
              onChange={(e) => setAcaoDesc(e.target.value)}
              placeholder="ex: Enviar proposta detalhada"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Próximo movimento recomendado
            </label>
            <textarea
              value={proxMovimento}
              onChange={(e) => setProxMovimento(e.target.value)}
              placeholder="Estratégia, ângulo, gancho..."
              rows={2}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={upsert.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {upsert.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {oportunidade ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
